import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Pagination,
  Stack,
  Button,
  Tooltip,
  Avatar,
  alpha,
  useTheme,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ReceiptIcon from "@mui/icons-material/Receipt";
import { GlobalContext } from "../../context/GlobalContext";
import { TranslateContext } from "../../context/TranslateContext";
import PageHeader from "../../common/PageHeader";

const Orders = () => {
  const { hitAxios } = React.useContext(GlobalContext);
  const { lang } = React.useContext(TranslateContext);
  const theme = useTheme();
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const limit = 20;

  useEffect(() => {
    loadOrders();
  }, [page]);

  const loadOrders = async () => {
    const res = await hitAxios({
      path: `/api/orders/admin-get-all?page=${page}&limit=${limit}`,
      admin: true,
      showSnackbar: false,
    });
    if (res?.data?.success) {
      setOrders(res.data.data);
      setTotal(res.data.total);
    }
  };
  const confirmOffline = async (uid) => {
    await hitAxios({
      path: "/api/orders/admin-confirm-offline",
      post: true,
      admin: true,
      obj: { order_uid: uid },
    });
    loadOrders();
  };

  const statusColor = (s) =>
    ({ completed: "success", pending: "warning", failed: "error" })[s] ||
    "default";
  const methodColors = {
    paypal: "#0070BA",
    stripe: "#635BFF",
    offline: "#10B981",
  };

  return (
    <Box>
      <PageHeader
        title={lang.orders || "Orders"}
        subtitle={`${total} total orders`}
        icon={ReceiptIcon}
      />

      <Card>
        <CardContent sx={{ p: 0 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {[
                    "Order",
                    "User",
                    "Plan",
                    "Amount",
                    "Method",
                    "Status",
                    "Date",
                    "Action",
                  ].map((h) => (
                    <TableCell key={h}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {orders.map((order) => (
                  <TableRow
                    key={order.id}
                    sx={{
                      "&:hover": {
                        bgcolor: alpha(theme.palette.primary.main, 0.03),
                      },
                    }}
                  >
                    <TableCell>
                      <Typography
                        variant="caption"
                        fontFamily="monospace"
                        color="text.secondary"
                      >
                        #{order.uid?.substring(0, 8)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Avatar
                          sx={{
                            width: 28,
                            height: 28,
                            bgcolor: theme.palette.primary.main,
                            fontSize: 11,
                            fontWeight: 700,
                          }}
                        >
                          {order.user_name?.[0]?.toUpperCase() || "U"}
                        </Avatar>
                        <Box>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{ lineHeight: 1.2 }}
                          >
                            {order.user_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {order.user_email}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{order.plan_name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        color="primary.main"
                      >
                        ${order.amount}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={order.payment_method || "N/A"}
                        size="small"
                        sx={{
                          bgcolor: alpha(
                            methodColors[order.payment_method] || "#888",
                            0.12,
                          ),
                          color: methodColors[order.payment_method] || "#888",
                          fontWeight: 700,
                          height: 22,
                          fontSize: "0.7rem",
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={order.status}
                        size="small"
                        color={statusColor(order.status)}
                        sx={{ fontWeight: 600, height: 22, fontSize: "0.7rem" }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(order.createdAt).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {order.payment_method === "offline" &&
                        order.status === "pending" && (
                          <Tooltip title="Confirm offline payment & activate plan">
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              startIcon={<CheckCircleIcon />}
                              onClick={() => confirmOffline(order.uid)}
                              sx={{ borderRadius: 2, fontSize: "0.7rem" }}
                            >
                              Confirm
                            </Button>
                          </Tooltip>
                        )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box
            sx={{
              p: 2,
              display: "flex",
              justifyContent: "center",
              borderTop: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Pagination
              count={Math.ceil(total / limit)}
              page={page}
              onChange={(_, v) => setPage(v)}
              color="primary"
              shape="rounded"
            />
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Orders;
